/**
 * LOCKLINE+ 快速估價試算（表價來自經銷商版價目）
 * 純函數，供畫面與單元測試共用。
 */

function findPlan(catalog, planCode) {
  return catalog.plans.find((p) => p.code === planCode) || null;
}

function findBillingCycle(catalog, code) {
  return catalog.billingCycles.find((c) => c.code === code) || null;
}

/**
 * 企業版表價月費；超過專案門檻回傳 null
 * @param {number} effectiveFriends
 * @param {object} catalog
 * @returns {number|null}
 */
export function resolveEnterpriseListMonthly(effectiveFriends, catalog) {
  const plan = findPlan(catalog, 'enterprise');
  if (!plan?.tiers?.length) return null;

  const friends = Number(effectiveFriends) || 0;
  if (friends > (plan.projectQuoteAboveFriends ?? Infinity)) {
    return null;
  }

  for (const tier of plan.tiers) {
    if (friends <= tier.maxFriends) {
      return tier.monthlyPrice;
    }
  }
  return null;
}

/**
 * 好友數超過目前方案時，建議改報上限剛好涵蓋的下一檔方案。
 * @returns {{ planCode: string, planName: string, listMonthlyPrice: number|null, message: string }|null}
 */
export function suggestUpgrade({ planCode, effectiveFriends, catalog }) {
  const plan = findPlan(catalog, planCode);
  const friends = Number(effectiveFriends) || 0;
  if (!plan || plan.maxFriends == null || friends <= plan.maxFriends) return null;

  const fitting = [...(catalog.plans || [])]
    .filter((item) => item.code !== plan.code)
    .filter((item) => item.maxFriends == null || Number(item.maxFriends) >= friends)
    .sort((a, b) => {
      const aUnlimited = a.maxFriends == null;
      const bUnlimited = b.maxFriends == null;
      if (aUnlimited !== bUnlimited) return aUnlimited ? 1 : -1;
      if (!aUnlimited && Number(a.maxFriends) !== Number(b.maxFriends)) {
        return Number(a.maxFriends) - Number(b.maxFriends);
      }
      if (a.code === 'enterprise') return -1;
      if (b.code === 'enterprise') return 1;
      const aPrice = a.monthlyPrice == null ? Infinity : Number(a.monthlyPrice);
      const bPrice = b.monthlyPrice == null ? Infinity : Number(b.monthlyPrice);
      return aPrice - bPrice;
    })[0];
  if (!fitting) return null;

  const isEnterprise = fitting.code === 'enterprise' || fitting.maxFriends == null;
  const listMonthlyPrice = isEnterprise
    ? resolveEnterpriseListMonthly(friends, catalog)
    : fitting.monthlyPrice;
  const priceText = listMonthlyPrice == null
    ? '專案報價（請自填月費）'
    : `表價月費 NT$${Number(listMonthlyPrice).toLocaleString('zh-TW')}`;
  const limitText = fitting.maxFriends == null
    ? ''
    : `，上限 ${Number(fitting.maxFriends).toLocaleString('zh-TW')}`;

  return {
    planCode: fitting.code,
    planName: fitting.name,
    listMonthlyPrice,
    message: `有效好友數已超過${plan.name}上限（${Number(plan.maxFriends).toLocaleString('zh-TW')}），建議改報${fitting.name}（${priceText}${limitText}）`
  };
}

function listPlanMonthly(plan, effectiveFriends, catalog) {
  if (!plan) return null;
  if (plan.code === 'enterprise') {
    return resolveEnterpriseListMonthly(effectiveFriends, catalog);
  }
  return plan.monthlyPrice;
}

/** 企業版地板價（第一級距月費） */
export function resolveEnterpriseBaseMonthly(catalog) {
  const plan = findPlan(catalog, 'enterprise');
  const base = plan?.tiers?.[0]?.monthlyPrice;
  return base == null ? null : Number(base);
}

/**
 * 將企業版總月費拆成基本月費 + 好友級距加價
 * @returns {{ base: number|null, friendsSurcharge: number|null }}
 */
export function splitEnterpriseMonthly(totalMonthly, catalog) {
  const base = resolveEnterpriseBaseMonthly(catalog);
  if (totalMonthly == null || Number.isNaN(Number(totalMonthly))) {
    return { base: null, friendsSurcharge: null };
  }
  const total = Number(totalMonthly);
  if (base == null) {
    return { base: total, friendsSurcharge: 0 };
  }
  return {
    base,
    friendsSurcharge: Math.max(0, total - base)
  };
}

function hasOverrideValue(value) {
  return value != null && value !== '';
}

function enterpriseTotalFromSplit(split) {
  if (
    split == null
    || split.base == null
    || Number.isNaN(Number(split.base))
    || split.friendsSurcharge == null
    || Number.isNaN(Number(split.friendsSurcharge))
  ) {
    return null;
  }
  return Number(split.base) + Number(split.friendsSurcharge);
}

/**
 * 企業版報價拆帳：可分別覆寫基本月費／好友加價，或覆寫總月費後再拆。
 * @returns {{ base: number|null, friendsSurcharge: number|null }}
 */
function resolveEnterpriseQuotedSplit(listSplit, listPlanMonthlyPrice, overrides, catalog) {
  const baseOverridden = hasOverrideValue(overrides.enterpriseBase);
  const friendsOverridden = hasOverrideValue(overrides.enterpriseFriendsSurcharge);

  if (baseOverridden || friendsOverridden) {
    return {
      base: baseOverridden ? Number(overrides.enterpriseBase) : listSplit.base,
      friendsSurcharge: friendsOverridden
        ? Number(overrides.enterpriseFriendsSurcharge)
        : listSplit.friendsSurcharge
    };
  }

  if (hasOverrideValue(overrides.monthlySubscription)) {
    return splitEnterpriseMonthly(Number(overrides.monthlySubscription), catalog);
  }

  if (listPlanMonthlyPrice == null) {
    // 專案報價：基本月費仍帶出地板價，好友加價待自填
    return { base: listSplit.base, friendsSurcharge: null };
  }

  return splitEnterpriseMonthly(listPlanMonthlyPrice, catalog);
}

function calcPointsImportFee(pointsImport, catalog) {
  if (!pointsImport?.enabled) {
    return { amount: 0, lines: [] };
  }

  const cfg = catalog.pointsImport || {};
  const lines = [];
  let amount = 0;
  const importName = cfg.name || '舊會員資料匯入';

  // 標準格式匯入本身不收費
  lines.push({
    code: 'points_import_base',
    name: `${importName}（免費）`,
    quantity: 1,
    listAmount: 0,
    quotedAmount: 0
  });

  if (pointsImport.needsCleaning) {
    const cleaning = Number(pointsImport.cleaningFee);
    const cleaningFee = Number.isFinite(cleaning) ? cleaning : Number(cfg.cleaningFeeFrom) || 0;
    amount += cleaningFee;
    lines.push({
      code: 'points_import_cleaning',
      name: '資料清洗處理費',
      quantity: 1,
      listAmount: Number(cfg.cleaningFeeFrom) || 0,
      quotedAmount: cleaningFee
    });
  }

  return { amount, lines };
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** 訂閱月費可在報價單改價的選購模組 */
export const ADJUSTABLE_MODULE_CODES = ['marketing_ops'];

/** 年繳不享兩個月優惠，按實際月數（12）計 */
export const NO_YEARLY_DISCOUNT_MODULE_CODES = ['content_publishing', 'marketing_ops'];

/** 報價單「加購」區塊固定順序：簡訊 → AI → DDPay → 星益欣（緊鄰、不穿插導入項） */
export const SERVICE_FEE_ADDON_CODES = [
  'sms_points',
  'ai_credit_topup',
  'ddpay',
  'xingyixin_einvoice'
];

/**
 * 將一次性項目與年費選購排成報價單列印順序：
 * 導入／OA／匯入等在前，加購四項依 SERVICE_FEE_ADDON_CODES 連在一起。
 * @param {Array<{code: string}>} oneTimeItems
 * @param {Array<{code: string}>} annualItems
 */
export function orderServiceFeeItems(oneTimeItems = [], annualItems = []) {
  const all = [...oneTimeItems, ...annualItems];
  const bundle = [];
  const custom = [];
  const rest = [];
  for (const item of all) {
    if (SERVICE_FEE_ADDON_CODES.includes(item?.code)) {
      bundle.push(item);
    } else if (String(item?.code || '').startsWith('custom_')) {
      custom.push(item);
    } else {
      rest.push(item);
    }
  }
  bundle.sort(
    (a, b) => SERVICE_FEE_ADDON_CODES.indexOf(a.code) - SERVICE_FEE_ADDON_CODES.indexOf(b.code)
  );
  return [...rest, ...bundle, ...custom];
}

/**
 * @typedef {object} QuoteInput
 * @property {string} planCode
 * @property {number} effectiveFriends
 * @property {string} billingCycle
 * @property {string[]} [moduleCodes]
 * @property {string[]} [annualAddonCodes] 年費選購（金流／發票等）
 * @property {number} [chatdeskExtraSeats] ChatDesk 加購座席數（超出內含席）
 * @property {{ extraLineOa?: number, extraRichMenus?: number, extraTrainingSessions?: number }} [enterpriseAddons]
 * @property {{ enabled?: boolean, records?: number, needsCleaning?: boolean, cleaningFee?: number, extraSources?: number }} [pointsImport]
 * @property {{ enabled?: boolean, points?: number }} [smsPoints]
 * @property {boolean} [premiumSetup] 加值版建置服務（與導入費／建置費互斥）
 * @property {Array<{ id?: string, name?: string, description?: string, quantity?: number, unitPrice?: number }>} [customItems] 客製報價項目（一次性，不計次年續約）
 * @property {{ enabled?: boolean, amount?: number }} [aiCreditTopup]
 * @property {{ monthlySubscription?: number|null, enterpriseBase?: number|null, enterpriseFriendsSurcharge?: number|null, setupFee?: number|null, oneTimeAmounts?: Record<string, number|string>, moduleAmounts?: Record<string, number|string> }} [overrides]
 * @property {object} catalog
 */

/**
 * @param {QuoteInput} input
 */
export function calculateQuote(input) {
  const catalog = input.catalog;
  const plan = findPlan(catalog, input.planCode);
  const cycle = findBillingCycle(catalog, input.billingCycle || 'yearly');
  const priceMonths = cycle?.priceMonths ?? 1;
  const termMonths = cycle?.months ?? priceMonths;
  const chargeMonthsFor = (moduleCode) => (
    NO_YEARLY_DISCOUNT_MODULE_CODES.includes(moduleCode) ? termMonths : priceMonths
  );
  const friends = Number(input.effectiveFriends) || 0;
  const moduleCodes = Array.isArray(input.moduleCodes) ? input.moduleCodes : [];
  const annualAddonCodesRaw = Array.isArray(input.annualAddonCodes) ? input.annualAddonCodes : [];
  // 依價目表順序輸出，避免勾選順序造成 DDPay／星益欣不相鄰
  const annualAddonOrder = (catalog.annualAddons || []).map((a) => a.code);
  const annualAddonCodes = [
    ...annualAddonOrder.filter((code) => annualAddonCodesRaw.includes(code)),
    ...annualAddonCodesRaw.filter((code) => !annualAddonOrder.includes(code))
  ];
  const overrides = input.overrides || {};

  const subscriptionItems = [];
  const annualItems = [];
  const oneTimeItems = [];

  const listPlanMonthlyPrice = listPlanMonthly(plan, friends, catalog);

  const moduleAmountOverrides = overrides.moduleAmounts || {};
  let modulesMonthlyList = 0;
  let modulesMonthlyQuoted = 0;
  for (const code of moduleCodes) {
    const mod = catalog.modules.find((m) => m.code === code);
    if (!mod) continue;
    const listPrice = Number(mod.monthlyPrice) || 0;
    const rawOverride = moduleAmountOverrides[mod.code];
    const quotedPrice = hasOverrideValue(rawOverride) && Number.isFinite(Number(rawOverride))
      ? Number(rawOverride)
      : listPrice;
    modulesMonthlyList += listPrice;
    modulesMonthlyQuoted += quotedPrice;
    subscriptionItems.push({
      code: `module_${mod.code}`,
      name: mod.description ? `${mod.name}（${mod.description}）` : mod.name,
      quantity: 1,
      listAmount: listPrice,
      quotedAmount: quotedPrice,
      kind: 'subscription',
      adjustable: ADJUSTABLE_MODULE_CODES.includes(mod.code),
      chargeMonths: chargeMonthsFor(mod.code),
      noYearlyDiscount: NO_YEARLY_DISCOUNT_MODULE_CODES.includes(mod.code)
    });
  }

  // ChatDesk 加購座席（模組已勾選時才計入）
  const chatdeskMod = catalog.modules.find((m) => m.code === 'chatdesk');
  const chatdeskExtraSeats = moduleCodes.includes('chatdesk')
    ? Math.max(0, Number(input.chatdeskExtraSeats) || 0)
    : 0;
  if (chatdeskMod && chatdeskExtraSeats > 0) {
    const seatPrice = Number(chatdeskMod.extraSeatMonthlyPrice) || 0;
    const seatsAmount = chatdeskExtraSeats * seatPrice;
    modulesMonthlyList += seatsAmount;
    modulesMonthlyQuoted += seatsAmount;
    subscriptionItems.push({
      code: 'module_chatdesk_extra_seats',
      name: `ChatDesk 加購座席（超出內含 ${chatdeskMod.includedSeats ?? 3} 席）`,
      quantity: chatdeskExtraSeats,
      unitLabel: '席',
      listAmount: seatsAmount,
      quotedAmount: seatsAmount,
      kind: 'subscription',
      chargeMonths: priceMonths
    });
  }

  let planQuotedMonthly = null;
  let enterpriseSplit = null;

  // 方案本體列（企業版拆成基本月費 + 好友級距加價）
  if (plan?.code === 'enterprise') {
    const floor = resolveEnterpriseBaseMonthly(catalog);
    const isProject = listPlanMonthlyPrice == null;
    const listSplit = isProject
      ? { base: floor, friendsSurcharge: null }
      : splitEnterpriseMonthly(listPlanMonthlyPrice, catalog);
    const quotedSplit = resolveEnterpriseQuotedSplit(
      listSplit,
      listPlanMonthlyPrice,
      overrides,
      catalog
    );
    planQuotedMonthly = enterpriseTotalFromSplit(quotedSplit);
    enterpriseSplit = { list: listSplit, quoted: quotedSplit };

    subscriptionItems.unshift({
      code: 'plan_enterprise_friends',
      name: isProject
        ? `好友數／專案加價（${friends.toLocaleString('zh-TW')} 有效好友）`
        : `好友級距加價（${friends.toLocaleString('zh-TW')} 有效好友）`,
      quantity: 1,
      listAmount: listSplit.friendsSurcharge,
      quotedAmount: quotedSplit.friendsSurcharge,
      kind: 'subscription',
      chargeMonths: priceMonths
    });
    subscriptionItems.unshift({
      code: 'plan_enterprise_base',
      name: plan.baseFeeLabel
        || '企業版基本月費（會員／門市／員工帳號無上限；含 API、POS；LINE OA 1 個）',
      quantity: 1,
      listAmount: listSplit.base,
      quotedAmount: quotedSplit.base,
      kind: 'subscription',
      chargeMonths: priceMonths
    });
  } else if (plan) {
    planQuotedMonthly = hasOverrideValue(overrides.monthlySubscription)
      ? Number(overrides.monthlySubscription)
      : listPlanMonthlyPrice;
    subscriptionItems.unshift({
      code: `plan_${plan.code}`,
      name: plan.name,
      quantity: 1,
      listAmount: listPlanMonthlyPrice,
      quotedAmount: planQuotedMonthly == null || Number.isNaN(planQuotedMonthly)
        ? null
        : planQuotedMonthly,
      kind: 'subscription',
      negotiated: listPlanMonthlyPrice == null,
      chargeMonths: priceMonths
    });
  }

  const planMonthlyMissing = planQuotedMonthly == null || Number.isNaN(planQuotedMonthly);
  const requiresCustomEnterprisePrice =
    plan?.code === 'enterprise' && listPlanMonthlyPrice == null && planMonthlyMissing;
  const requiresCustomPlanPrice =
    Boolean(plan) && plan.code !== 'enterprise' && listPlanMonthlyPrice == null && planMonthlyMissing;

  const quotedMonthlySubscription =
    planQuotedMonthly == null || Number.isNaN(planQuotedMonthly)
      ? null
      : planQuotedMonthly + modulesMonthlyQuoted;

  const billingPeriodAmount = quotedMonthlySubscription == null
    ? 0
    : roundMoney(subscriptionItems.reduce((sum, item) => {
      const amount = Number(item.quotedAmount);
      if (!Number.isFinite(amount)) return sum;
      const months = Number(item.chargeMonths) || priceMonths;
      return sum + amount * months;
    }, 0));
  const hasFullYearModules = subscriptionItems.some(
    (item) => item.noYearlyDiscount && Number(item.chargeMonths) !== priceMonths
  );
  const periodLabel = hasFullYearModules
    ? `訂閱週期應付（${cycle?.name || '年繳'}；方案與其他月費 × ${priceMonths}，內容上架／行銷代操 × ${termMonths}）`
    : `訂閱週期應付（${cycle?.name || '—'}，月費合計 × ${priceMonths}）`;

  // 導入費／建置費，與加值版建置服務互斥（加值版已含基本導入）
  const listSetup = plan?.setupFee ?? 0;
  const premiumCfg = catalog.premiumSetup;
  const usePremiumSetup = Boolean(input.premiumSetup) && premiumCfg;
  if (usePremiumSetup) {
    const amount = Number(premiumCfg.price) || 0;
    const desc = premiumCfg.description ? `（${premiumCfg.description}）` : '';
    oneTimeItems.push({
      code: premiumCfg.code || 'premium_setup',
      name: `${premiumCfg.name}${desc}`,
      quantity: 1,
      listAmount: amount,
      quotedAmount: amount,
      kind: 'one_time'
    });
  } else {
    const quotedSetup = overrides.setupFee != null && overrides.setupFee !== ''
      ? Number(overrides.setupFee)
      : listSetup;
    if (plan) {
      const title = plan.setupFeeLabel || `${plan.name}導入費`;
      const includes = plan.setupFeeIncludes ? String(plan.setupFeeIncludes).trim() : '';
      oneTimeItems.push({
        code: 'setup_fee',
        name: includes ? `${title}（含：${includes}）` : title,
        quantity: 1,
        listAmount: listSetup,
        quotedAmount: Number.isNaN(Number(quotedSetup)) ? listSetup : Number(quotedSetup),
        kind: 'one_time'
      });
    }
  }

  // 企業版加購
  if (plan?.code === 'enterprise') {
    const addons = input.enterpriseAddons || {};
    const qtyMap = {
      extra_line_oa: Number(addons.extraLineOa) || 0,
      extra_rich_menu: Number(addons.extraRichMenus) || 0,
      extra_training: Number(addons.extraTrainingSessions) || 0
    };
    for (const addon of catalog.enterpriseAddons) {
      const qty = qtyMap[addon.code] || 0;
      if (qty <= 0) continue;
      const amount = qty * addon.unitPrice;
      oneTimeItems.push({
        code: addon.code,
        name: addon.name,
        quantity: qty,
        unitLabel: addon.unitLabel,
        listAmount: amount,
        quotedAmount: amount,
        kind: 'one_time'
      });
    }
  }

  const points = calcPointsImportFee(input.pointsImport, catalog);
  oneTimeItems.push(...points.lines.map((l) => ({ ...l, kind: 'one_time' })));

  // 加購簡訊點數（1 點 = unitPrice，預設 NT$1）
  const smsCfg = catalog.smsPoints;
  const smsInput = input.smsPoints || {};
  const smsPointsQty = smsInput.enabled ? Math.max(0, Math.floor(Number(smsInput.points) || 0)) : 0;
  if (smsCfg && smsPointsQty > 0) {
    const unit = Number(smsCfg.unitPrice) || 1;
    const amount = smsPointsQty * unit;
    oneTimeItems.push({
      code: 'sms_points',
      name: `${smsCfg.name}（1 點 = NT$${unit}）`,
      quantity: smsPointsQty,
      unitLabel: smsCfg.unitLabel || '點',
      listAmount: amount,
      quotedAmount: amount,
      kind: 'one_time'
    });
  }

  // AI 儲值（ChatMate 對話額度；最小單位／級距 minAmount）
  const aiCfg = catalog.aiCreditTopup;
  const aiInput = input.aiCreditTopup || {};
  if (aiCfg && aiInput.enabled) {
    const minAmount = Math.max(1, Number(aiCfg.minAmount) || 1000);
    const stepAmount = Math.max(1, Number(aiCfg.stepAmount) || minAmount);
    let amount = Math.max(0, Number(aiInput.amount) || 0);
    if (amount > 0) {
      if (amount < minAmount) amount = minAmount;
      // 對齊最小單位倍數
      amount = Math.ceil(amount / stepAmount) * stepAmount;
      oneTimeItems.push({
        code: 'ai_credit_topup',
        name: `${aiCfg.name}（最小單位 NT$${minAmount.toLocaleString('zh-TW')}）`,
        quantity: 1,
        listAmount: amount,
        quotedAmount: amount,
        kind: 'one_time'
      });
    }
  }

  // 年費選購（金流／發票等；首年與續約皆計入）
  for (const code of annualAddonCodes) {
    const addon = (catalog.annualAddons || []).find((a) => a.code === code);
    if (!addon) continue;
    const yearly = Number(addon.yearlyPrice);
    const amount = Number.isFinite(yearly) ? yearly : 0;
    annualItems.push({
      code: addon.code,
      name: addon.description ? `${addon.name}（${addon.description}）` : addon.name,
      quantity: 1,
      unitLabel: '年',
      listAmount: amount,
      quotedAmount: amount,
      kind: 'annual'
    });
  }

  // 客製報價項目（一次性；名稱空白的列略過）
  const customItems = Array.isArray(input.customItems) ? input.customItems : [];
  customItems.forEach((raw, index) => {
    const name = String(raw?.name || '').trim();
    if (!name) return;
    const description = String(raw?.description || '').trim();
    const quantity = Math.max(1, Math.floor(Number(raw?.quantity) || 1));
    const unitPrice = Math.max(0, Number(raw?.unitPrice) || 0);
    const amount = roundMoney(quantity * unitPrice);
    const id = String(raw?.id || index + 1).replace(/[^\w-]/g, '') || String(index + 1);
    oneTimeItems.push({
      code: `custom_${id}`,
      name: description ? `${name}（${description}）` : name,
      quantity,
      listAmount: amount,
      quotedAmount: amount,
      kind: 'one_time'
    });
  });

  // 一次性／年費人工覆寫（依項目 code）
  const oneTimeAmountOverrides = overrides.oneTimeAmounts || {};
  for (const item of [...oneTimeItems, ...annualItems]) {
    const raw = oneTimeAmountOverrides[item.code];
    if (raw === undefined || raw === null || raw === '') continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    item.quotedAmount = n;
  }

  const oneTimeTotal = oneTimeItems.reduce((sum, item) => sum + (Number(item.quotedAmount) || 0), 0);
  const annualTotal = annualItems.reduce((sum, item) => sum + (Number(item.quotedAmount) || 0), 0);
  const subtotalExclTax = roundMoney(billingPeriodAmount + oneTimeTotal + annualTotal);
  const taxAmount = roundMoney(subtotalExclTax * (catalog.taxRate || 0));
  const totalInclTax = roundMoney(subtotalExclTax + taxAmount);

  // 次年續約：訂閱週期應付＋年費選購（不含導入／加購／點數匯入）
  const renewalExclTax = roundMoney(billingPeriodAmount + annualTotal);
  const renewalTaxAmount = roundMoney(renewalExclTax * (catalog.taxRate || 0));
  const renewalInclTax = roundMoney(renewalExclTax + renewalTaxAmount);

  const isDeviated = (() => {
    if (
      hasOverrideValue(overrides.monthlySubscription)
      && Number(overrides.monthlySubscription) !== listPlanMonthlyPrice
    ) {
      return true;
    }
    if (
      enterpriseSplit
      && (
        (
          hasOverrideValue(overrides.enterpriseBase)
          && Number(overrides.enterpriseBase) !== Number(enterpriseSplit.list.base)
        )
        || (
          hasOverrideValue(overrides.enterpriseFriendsSurcharge)
          && Number(overrides.enterpriseFriendsSurcharge)
            !== Number(enterpriseSplit.list.friendsSurcharge)
        )
      )
    ) {
      return true;
    }
    if (
      !usePremiumSetup
      && hasOverrideValue(overrides.setupFee)
      && Number(overrides.setupFee) !== listSetup
    ) {
      return true;
    }
    for (const item of [...subscriptionItems, ...oneTimeItems, ...annualItems]) {
      if (item.listAmount != null && item.quotedAmount != null
        && Number(item.listAmount) !== Number(item.quotedAmount)) {
        return true;
      }
    }
    return false;
  })();

  const upgradeSuggestion = suggestUpgrade({
    planCode: input.planCode,
    effectiveFriends: friends,
    catalog
  });

  const exceedsPlanFriendLimit = Boolean(
    plan && plan.code !== 'enterprise' && plan.maxFriends != null && friends > plan.maxFriends
  );

  const serviceFeeItems = orderServiceFeeItems(oneTimeItems, annualItems);

  return {
    catalogVersion: catalog.version,
    plan,
    billingCycle: cycle,
    listMonthlySubscription: listPlanMonthlyPrice == null && modulesMonthlyList === 0
      ? null
      : (listPlanMonthlyPrice == null ? modulesMonthlyList : listPlanMonthlyPrice + modulesMonthlyList),
    quotedMonthlySubscription,
    enterpriseBaseMonthly: enterpriseSplit?.quoted?.base ?? null,
    enterpriseFriendsSurcharge: enterpriseSplit?.quoted?.friendsSurcharge ?? null,
    listEnterpriseBaseMonthly: enterpriseSplit?.list?.base ?? null,
    listEnterpriseFriendsSurcharge: enterpriseSplit?.list?.friendsSurcharge ?? null,
    billingPeriodAmount,
    periodLabel,
    oneTimeTotal,
    annualTotal,
    subscriptionItems,
    annualItems,
    oneTimeItems,
    serviceFeeItems,
    subtotalExclTax,
    taxAmount,
    totalInclTax,
    renewalExclTax,
    renewalTaxAmount,
    renewalInclTax,
    taxRate: catalog.taxRate,
    isDeviated,
    requiresCustomEnterprisePrice,
    requiresCustomPlanPrice,
    exceedsPlanFriendLimit,
    upgradeSuggestion,
    canExport: !requiresCustomEnterprisePrice && !requiresCustomPlanPrice,
    terms: catalog.terms || []
  };
}
